import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { Locale } from '@macksons/i18n';
import { resolveScope, type OperatorScope } from './auth/operator-scope';
import type { SessionState } from './auth/session';
import type { ConnectivityState } from './connectivity/connectivity';
import type { ScanQueueSnapshot } from './offline/scan-queue';
import type { PwaState } from './pwa/register-sw';
import { useServices } from './services';

export function useLocale(): Locale {
  const { localeStore } = useServices();
  return useSyncExternalStore(localeStore.subscribe, localeStore.get);
}

/** Translator bound to the current locale; components re-render when the locale changes. */
export function useT(): (key: string, values?: Record<string, unknown>) => string {
  const { i18n } = useServices();
  const locale = useLocale();
  return useCallback(
    (key: string, values?: Record<string, unknown>) => i18n.t(key, { ...values, lng: locale }),
    [i18n, locale],
  );
}

export function useSession(): SessionState {
  const { session } = useServices();
  return useSyncExternalStore(session.subscribe, session.get);
}

export function useConnectivity(): ConnectivityState {
  const { connectivity } = useServices();
  return useSyncExternalStore(connectivity.subscribe, connectivity.get);
}

export function usePwaState(): PwaState {
  const { pwa } = useServices();
  return useSyncExternalStore(pwa.subscribe, pwa.get);
}

const NO_QUEUE: ScanQueueSnapshot = { loaded: true, items: [], pendingCount: 0, flushing: false };
const noopSubscribe = () => () => undefined;
const noQueue = () => NO_QUEUE;

/**
 * Offline scan queue state for the UI: items in scan order (pending, `done` with their
 * ClaimResult, `rejected` with their problem for review), pending count, whether a flush
 * is running and the last flush report.
 */
export function useScanQueue(): ScanQueueSnapshot {
  const { scanQueue } = useServices();
  return useSyncExternalStore(scanQueue?.subscribe ?? noopSubscribe, scanQueue?.getSnapshot ?? noQueue);
}

/**
 * Effective tenant and outlet of the signed-in operator. Keeps the stores (which feed
 * `X-Tenant-Id` and the `outletId` of ops requests) in line with the token's assignments.
 */
export function useOperatorScope(): OperatorScope | undefined {
  const { tenantStore, outletStore } = useServices();
  const session = useSession();
  const storedTenant = useSyncExternalStore(tenantStore.subscribe, tenantStore.get);
  const storedOutlet = useSyncExternalStore(outletStore.subscribe, outletStore.get);
  const claims = session.status === 'signedIn' ? session.claims : undefined;
  const scope = useMemo(
    () => (claims ? resolveScope(claims, storedTenant, storedOutlet) : undefined),
    [claims, storedTenant, storedOutlet],
  );
  useEffect(() => {
    if (!scope) return;
    if (scope.tenantId !== storedTenant) tenantStore.set(scope.tenantId);
    if (scope.outletId !== storedOutlet) outletStore.set(scope.outletId);
  }, [scope, storedTenant, storedOutlet, tenantStore, outletStore]);
  return scope;
}
